/**
 * @file order-now.ts
 * @description Модель полосы «Сейчас»: чей ход, что написано, какая кнопка
 * (phase19.24-order-card-chain-design.md §13, §14).
 *
 * Считаем один раз на странице и раздаём полосе, степперу, шапке и блоку
 * «Что дальше» — иначе главная кнопка в шапке и в полосе разъезжаются
 * (правило DS §5.3: кнопка одна, показана в двух местах).
 *
 * Цепочки нет (сервер не ответил) — модели нет: карточка обязана работать без
 * агрегата, шапка в этом случае берёт действия из статуса заказа.
 */

import type { DeliveryAgreementDto } from "./api-delivery-agreement";
import type { OrderDto } from "./api-orders";
import {
  ORDER_ACTION_META,
  fallbackPrimaryActionKey,
  isActionAllowedInStatus,
  type OrderActionKey,
} from "./order-actions";
import {
  currentStage,
  currentStageKind,
  isPickupRoute,
  nextManagerStage,
  type OrderChainDto,
  type OrderChainOwner,
  type OrderRouteStageDto,
} from "./order-chain";
import {
  ORDER_CARD_ANCHOR,
  orderCardPath,
  orderTabHref,
  resolveChainLink,
  type ChainLinkContext,
} from "./order-chain-links";
import {
  ORDER_CHAIN_ALERT_META,
  ORDER_CHAIN_OWNER_META,
  type OrderChainAlert,
} from "./order-chain-owner";
import {
  formatSince,
  needsDeliveryAgreement,
  ORDER_NOW_PROBLEM_TEXTS,
  ORDER_NOW_TEXTS,
  type NowPrimary,
  type NowPrimaryTarget,
  type NowText,
} from "./order-chain-texts";
import { nowStripTone, type NowStripTone } from "./order-chain-tone";

export interface OrderNowModel {
  stage: OrderRouteStageDto;
  owner: OrderChainOwner;
  isManagerTurn: boolean;
  tone: NowStripTone;
  /**
   * ОДИН красный стикер поверх владельца: проблема важнее просрочки (§14,
   * «не больше двух стикеров»). Просрочка при проблеме уходит текстом во
   * вторую строку, а не третьим бейджем.
   */
  alert: OrderChainAlert | null;
  /** Подпись красного стикера: «Просрочено · 18 ч» или «Нужно решение». */
  alertLabel: string | null;
  /**
   * Стикер владельца рисуем контуром: рядом с красным «Ваш ход» не должен
   * кричать тем же цветом.
   */
  ownerOutlined: boolean;
  /** «Просрочено · 2 дн»; null — в срок. */
  overdueLabel: string | null;
  /** Сколько заказ в стадии: «2 ч 15 мин». */
  sinceLabel: string | null;
  text: NowText;
  primary: NowPrimary | null;
  /**
   * Заказ сначала идёт через согласование доставки: «Подтвердить» и «К оплате»
   * сервер отвергнет. Считается один раз здесь — меню «⋯», главная кнопка и
   * полоса обязаны отвечать одинаково.
   */
  agreementFirst: boolean;
  nextManager: OrderRouteStageDto | null;
  /** «Ваш следующий шаг: Передать в ТК» — зелёным при чужом ходе. */
  nextStepHint: string | null;
  /** Для скринридера: «Ваш ход: Собран, 20 мин». */
  ariaLabel: string;
}

/** Модель «Сейчас» по заказу и цепочке; null — стадии/цепочки нет. */
export function buildOrderNow(
  order: OrderDto,
  chain: OrderChainDto | null,
  agreement?: DeliveryAgreementDto | null,
): OrderNowModel | null {
  const stage = currentStage(chain?.route ?? null);
  if (!chain || !stage) return null;

  // Проблему стадии (в т.ч. `ITEM_UNLINKED` по OQ-54) считает сервер: у него
  // есть состав задания и гварды проведения. Клиентский вывод по `order.items`
  // убран (TD-87) — карточка состав не запрашивает, и фолбэк был мёртвым.
  const problem = chain.route.current?.problem ?? null;
  const overdueSince = chain.route.current?.overdueSince ?? null;
  const nextManager = nextManagerStage(chain.route);
  const ctx = {
    order,
    stage,
    chain,
    pickup: isPickupRoute(chain.route),
    nextManager,
    // Заявка F30 различает шесть ситуаций согласования (шаг 5.1 §5):
    // без неё полоса говорила бы одно и то же и в «исход не выбран», и в
    // «клиент молчит третьи сутки».
    agreement: agreement ?? null,
  };

  // Вид стадии берём из `route.current.kind`: в шкале бывает точка-заглушка
  // «Получение» (способ получения не выбран), и искать по ней текст нельзя.
  const kind = currentStageKind(chain.route, stage);

  // Проблема перебивает текст стадии: сначала решаем её, потом идём дальше.
  const entry = problem
    ? ORDER_NOW_PROBLEM_TEXTS[problem]
    : kind
      ? (ORDER_NOW_TEXTS[kind] ?? null)
      : null;
  // Вида нет (заглушка маршрута) — говорим словами сервера: имя точки и её
  // подсказка. Свой текст здесь выдумывать нельзя: смысл знает сервер.
  const text: NowText = entry?.text(ctx) ?? {
    first: stage.name,
    second: stage.hint ?? null,
  };
  const primary = entry?.primary?.(ctx) ?? null;

  // Красный стикер ровно один: проблема перебивает просрочку. Полоса с тремя
  // бейджами («Просрочено», «Нужно решение», «Ваш ход») читалась как шум —
  // дефект QA 2026-09-21.
  const alert: OrderChainAlert | null = problem ? "PROBLEM" : overdueSince ? "OVERDUE" : null;

  const isManagerTurn = stage.owner === "MANAGER" || !!problem;
  const overdueAge = formatSince(overdueSince);
  const overdueLabel = overdueAge
    ? `Просрочено · ${overdueAge}`
    : overdueSince
      ? "Просрочено"
      : null;

  return {
    stage,
    owner: stage.owner,
    isManagerTurn,
    tone: nowStripTone({ isProblem: !!problem, isOverdue: !!overdueSince, isManagerTurn }),
    alert,
    alertLabel:
      alert === "OVERDUE"
        ? overdueLabel
        : alert
          ? ORDER_CHAIN_ALERT_META.PROBLEM.label
          : null,
    ownerOutlined: !!alert && stage.owner === "MANAGER",
    overdueLabel,
    sinceLabel: formatSince(stage.sinceAt),
    text,
    primary,
    agreementFirst: needsDeliveryAgreement({
      order,
      route: chain.route,
      agreement: agreement ?? null,
    }),
    nextManager,
    nextStepHint:
      !isManagerTurn && nextManager ? `Ваш следующий шаг: ${nextManager.name}` : null,
    // Озвучка читает подпись владельца из реестра, а не код перечисления:
    // «Ход: CUSTOMER» экранный диктор произносил по буквам (дефект QA 5.1).
    ariaLabel: `${
      isManagerTurn
        ? ORDER_CHAIN_OWNER_META.MANAGER.aria
        : ORDER_CHAIN_OWNER_META[stage.owner].aria
    }: ${stage.name}${
      formatSince(stage.sinceAt) ? `, ${formatSince(stage.sinceAt)}` : ""
    }`,
  };
}

/** Главная кнопка, приведённая к тому, что умеет UI: ссылка или действие. */
export interface ResolvedPrimaryAction {
  label: string;
  /** Куда ведёт кнопка-ссылка; null — кнопка вызывает действие заказа. */
  href: string | null;
  action: OrderActionKey | null;
}

/**
 * Превращает цель кнопки в адрес или имя действия. Недостижимая цель (узла
 * нет, ссылки нет) — `null`: лучше отсутствие кнопки, чем кнопка в никуда
 * (урок phase19.14 про «Упаковано», которое всегда возвращало 409).
 */
export function resolvePrimaryAction(
  primary: NowPrimary | null,
  chain: OrderChainDto | null,
  ctx: ChainLinkContext,
): ResolvedPrimaryAction | null {
  if (!primary || !ctx.slug) return null;
  const { label, target } = primary;
  switch (target.kind) {
    case "node": {
      const node = chain?.nodes.find((n) => n.key === target.node) ?? null;
      const href = resolveChainLink(node?.link ?? null, ctx);
      return href ? { label, href, action: null } : null;
    }
    case "tab":
      return { label, href: orderTabHref(ctx, target.tab, target.focusId), action: null };
    case "anchor":
      return { label, href: `${orderCardPath(ctx)}#${target.anchor}`, action: null };
    // Страница магазина вне заказа: «В настройки складов» у незаполненного
    // пункта самовывоза. Без slug ссылку не собрать — кнопки не будет.
    case "shop":
      return { label, href: `/shop/${ctx.slug}/${target.path}`, action: null };
    case "action":
      return { label, href: null, action: target.action };
    default:
      return null;
  }
}

/** Кнопка-действие по ключу реестра; null — ключа нет. */
function actionButton(key: OrderActionKey | null): ResolvedPrimaryAction | null {
  return key ? { label: ORDER_ACTION_META[key].label, href: null, action: key } : null;
}

/**
 * Запасная цель, когда основная недостижима: блок «Сборка» во вкладке
 * «Обзор». Он открыт и роли без складских прав (сервер отдаёт его по правам
 * заказа), поэтому со скрытым узлом `PICKING` менеджер всё равно доходит до
 * недостачи. Блока нет — нет и цели: стоит он только на стадии сборки.
 */
function pickingAnchorTarget(order: OrderDto): NowPrimaryTarget | null {
  if (order.stage?.kind !== "PACKAGING") return null;
  return { kind: "anchor", anchor: ORDER_CARD_ANCHOR.picking };
}

/**
 * Главная кнопка карточки целиком: цель текущей стадии, а если цепочка не
 * собралась — первый неопасный переход статуса (§8 «Путь не собрался»).
 *
 * Правило «нет цепочки — работаем по статусу» держим здесь, а не на странице:
 * тот же выбор нужен шапке, полосе «Сейчас» и sticky-бару, и три копии
 * условия разъехались бы на первой же новой стадии.
 *
 * Два правила прав (находки ревью 2026-09-21):
 *  1. цель-действие (`confirm`, «Начать сборку», «Выдать заказ», «Запросить
 *     отзыв») без `SHOP_MANAGE_ORDERS` не показываем вовсе — сервер ответит
 *     403, а роль на просмотр получала кнопку, которая только ругается.
 *     Ссылочные цели прав не требуют: их адресат сам проверит доступ;
 *  1a. цель-действие, которого нет в реестре переходов текущего СТАТУСА,
 *     тоже не показываем: шкала строится по стадиям воронки магазина и может
 *     разойтись со статусом заказа (инцидент 2026-09-21 — «Подтвердить» на
 *     заказе в «Ожидании оплаты», клик давал 409). Вместо неё — переход по
 *     статусу;
 *  2. цель, которая не разрешилась (узел скрыт правами и пришёл без ссылки),
 *     откатывается на якорь блока «Сборка», и только потом — на переход по
 *     статусу. Иначе недостача без складских прав оставляла карточку вообще
 *     без кнопки.
 */
export function orderPrimaryButton(
  order: OrderDto,
  now: OrderNowModel | null,
  chain: OrderChainDto | null,
  ctx: ChainLinkContext,
  canManage: boolean,
): ResolvedPrimaryAction | null {
  // Цепочки нет — ведём заказ по статусу (про согласование в этом случае
  // ничего не известно: признак живёт в модели «Сейчас»).
  const scope = { status: order.status, agreementFirst: now?.agreementFirst ?? false };
  if (!now) return actionButton(fallbackPrimaryActionKey(scope, canManage));

  const primary = now.primary;
  // Стадия намеренно без кнопки (ход склада, заказ отменён) — так и оставляем.
  if (!primary) return null;
  if (primary.target.kind === "action") {
    if (!canManage) return null;
    // Переход, которого статус не допускает, не показываем: сервер ответит
    // 409, и менеджер видит «кнопка ничего не делает». Вместо неё — честный
    // переход по статусу (или ничего).
    return isActionAllowedInStatus(scope, primary.target.action)
      ? resolvePrimaryAction(primary, chain, ctx)
      : actionButton(fallbackPrimaryActionKey(scope, canManage));
  }

  const resolved = resolvePrimaryAction(primary, chain, ctx);
  if (resolved) return resolved;

  const anchor = pickingAnchorTarget(order);
  const anchored = anchor
    ? resolvePrimaryAction({ label: primary.label, target: anchor }, chain, ctx)
    : null;
  return anchored ?? actionButton(fallbackPrimaryActionKey(scope, canManage));
}
